import { useEffect, useMemo, useState } from 'react'
import { MessagesLandingMenuItem } from './types'
import {
  deepCopy,
  DEFAULT_MENU_NAME,
  inferBackendUsesMenuKeys,
  parseMenuItems,
  parseMenuLayoutFromClipboard,
  resolveCustomMenuIdentity,
  serializeCustomMenuDefinition,
  serializeMenuLayoutForClipboard,
} from './utils'
import { toast } from 'sonner'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { useCurrentApplication } from '../useCurrentApplication'
import { SelectOption } from '@/components/form-components/SingleSelectCombobox'

type MessagesApplicationsApplicationPages = RestSchemas['Messages.Applications.ApplicationPages']

export function useMenuOperations(initialDefaultData: MessagesLandingMenuItem[]) {
  const { applicationId } = useCurrentApplication()
  const {
    postUiApiUpdateMenu,
    postUiApiSaveCustomMenu,
    deleteUiApiDeleteCustomMenu,
    getUiApiGetMenu,
    getUiApiGetCustomMenus,
    getUiApiGetCustomMenu,
    getLandingApiGetPlatformOperations,
    getAppPagesApiGetApplicationPages,
    postAppPagesApiSaveApplicationPages,
  } = useRestClient()

  const [selectedMenuKey, setSelectedMenuKey] = useState(DEFAULT_MENU_NAME)
  const [knownCustomMenuNames, setKnownCustomMenuNames] = useState<Record<string, string>>({})
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [savedMenuItems, setSavedMenuItems] = useState<MessagesLandingMenuItem[]>(() => initialDefaultData)
  const [removedPageIdsPendingConfirmation, setRemovedPageIdsPendingConfirmation] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [menuItems, setMenuItems] = useState<MessagesLandingMenuItem[]>(() => initialDefaultData)
  const hasChanged = JSON.stringify(savedMenuItems) !== JSON.stringify(menuItems)
  const [isExpanded, setIsExpanded] = useState(false)

  const isCustomMenu = selectedMenuKey !== DEFAULT_MENU_NAME

  const { data: customMenus, isFetching: isFetchingCustomMenus } = getUiApiGetCustomMenus.useQuery({
    path: { applicationId },
  })

  const backendUsesMenuKeys = useMemo(() => inferBackendUsesMenuKeys(customMenus), [customMenus])

  const resolveCustomMenuDisplayName = (menuKey: string) => {
    const knownName = knownCustomMenuNames[menuKey]?.trim()
    if (knownName) {
      return knownName
    }

    for (const menu of customMenus ?? []) {
      const identity = resolveCustomMenuIdentity(menu)
      if (identity?.key === menuKey && identity.name) {
        return identity.name
      }
    }

    return menuKey
  }

  const getCustomMenuApiPathParam = (menuKey: string, menuName?: string) => {
    if (backendUsesMenuKeys) {
      return menuKey
    }

    const displayName = menuName?.trim() || resolveCustomMenuDisplayName(menuKey)
    return displayName || menuKey
  }

  const customMenuApiPathParam = getCustomMenuApiPathParam(selectedMenuKey)

  const customMenuFromList = useMemo(() => {
    if (!isCustomMenu) {
      return undefined
    }

    const matches = (customMenus ?? []).filter(
      (menu) => resolveCustomMenuIdentity(menu)?.key === selectedMenuKey
    )

    if (!matches.length) {
      return undefined
    }

    return matches.reduce((best, menu) => {
      const bestItemCount = parseMenuItems(best.definition).length
      const menuItemCount = parseMenuItems(menu.definition).length

      if (menuItemCount !== bestItemCount) {
        return menuItemCount > bestItemCount ? menu : best
      }

      const bestUpdatedAt = best.updatedAt ? new Date(best.updatedAt).getTime() : 0
      const menuUpdatedAt = menu.updatedAt ? new Date(menu.updatedAt).getTime() : 0

      return menuUpdatedAt >= bestUpdatedAt ? menu : best
    })
  }, [customMenus, isCustomMenu, selectedMenuKey])

  const { data: customMenuData, isFetching: isFetchingCustomMenu, isPending: isPendingCustomMenu } =
    getUiApiGetCustomMenu.useQuery(
      { path: { applicationId, menuKey: customMenuApiPathParam } },
      { enabled: isCustomMenu && Boolean(applicationId) && backendUsesMenuKeys }
    )

  const resolvedCustomMenuData = customMenuFromList ?? customMenuData

  const loadedCustomMenuKey = resolvedCustomMenuData
    ? resolveCustomMenuIdentity(resolvedCustomMenuData)?.key
    : undefined

  const isLoadingCustomMenu =
    isCustomMenu &&
    loadedCustomMenuKey !== selectedMenuKey &&
    (isPendingCustomMenu || isFetchingCustomMenu || isFetchingCustomMenus)

  const { data: existingPages } = getAppPagesApiGetApplicationPages.useQuery({ query: { applicationId } })
  const { mutateAsync: saveMenu } = postUiApiUpdateMenu.useMutation()
  const { mutateAsync: saveCustomMenu } = postUiApiSaveCustomMenu.useMutation()
  const { mutateAsync: deleteCustomMenu, isPending: isDeletingCustomMenu } =
    deleteUiApiDeleteCustomMenu.useMutation()
  const { mutateAsync: savePages } = postAppPagesApiSaveApplicationPages.useMutation()

  const customMenuKeys = useMemo(() => {
    const keys = new Set<string>()

    for (const menu of customMenus ?? []) {
      const identity = resolveCustomMenuIdentity(menu)
      if (identity) {
        keys.add(identity.key)
      }
    }

    for (const key of Object.keys(knownCustomMenuNames)) {
      keys.add(key)
    }

    return Array.from(keys)
  }, [customMenus, knownCustomMenuNames])

  const menuOptions = useMemo<SelectOption<string>[]>(() => {
    const keyToName = new Map<string, string>()

    for (const menu of customMenus ?? []) {
      const identity = resolveCustomMenuIdentity(menu)
      if (identity) {
        keyToName.set(identity.key, identity.name)
      }
    }

    for (const [key, name] of Object.entries(knownCustomMenuNames)) {
      if (key) {
        keyToName.set(key, name)
      }
    }

    if (selectedMenuKey !== DEFAULT_MENU_NAME && !keyToName.has(selectedMenuKey)) {
      keyToName.set(selectedMenuKey, knownCustomMenuNames[selectedMenuKey] ?? selectedMenuKey)
    }

    return [
      { label: DEFAULT_MENU_NAME, value: DEFAULT_MENU_NAME },
      ...Array.from(keyToName.entries()).map(([key, name]) => ({ label: name, value: key })),
    ]
  }, [customMenus, knownCustomMenuNames, selectedMenuKey])

  useEffect(() => {
    if (!customMenus?.length) {
      return
    }

    setKnownCustomMenuNames((current) => {
      const next = { ...current }
      let changed = false

      for (const menu of customMenus) {
        const identity = resolveCustomMenuIdentity(menu)
        if (identity && identity.name && next[identity.key] !== identity.name) {
          next[identity.key] = identity.name
          changed = true
        }
      }

      return changed ? next : current
    })
  }, [customMenus])

  useEffect(() => {
    setExpandedItems([])
    setIsExpanded(false)
    setRemovedPageIdsPendingConfirmation([])

    if (!isCustomMenu) {
      setMenuItems(initialDefaultData)
      setSavedMenuItems(initialDefaultData)
      return
    }

    if (loadedCustomMenuKey !== selectedMenuKey) {
      setMenuItems([])
      setSavedMenuItems([])
      return
    }

    const items = parseMenuItems(resolvedCustomMenuData?.definition)
    setMenuItems(items)
    setSavedMenuItems(items)
  }, [selectedMenuKey, initialDefaultData, isCustomMenu, loadedCustomMenuKey, resolvedCustomMenuData?.definition])

  const invalidateCustomMenuQueries = async (menuKey: string) => {
    const apiPathParam = getCustomMenuApiPathParam(menuKey)

    await getUiApiGetCustomMenus.invalidateQueries({ parameters: { path: { applicationId } } })

    if (backendUsesMenuKeys) {
      await getUiApiGetCustomMenu.invalidateQueries({
        parameters: { path: { applicationId, menuKey: apiPathParam } },
      })
    }
  }

  const invalidateOnSuccess = async () => {
    if (isCustomMenu) {
      await invalidateCustomMenuQueries(selectedMenuKey)
    } else {
      await getUiApiGetMenu.invalidateQueries({ parameters: { path: { applicationId, grouping: 'menu' } } })
      await getLandingApiGetPlatformOperations.invalidateQueries({})
    }
  }

  const handleMenuSelectionChange = (menuKey: string) => {
    if (menuKey === selectedMenuKey) {
      return
    }

    if (hasChanged) {
      const shouldSwitch = window.confirm('You have unsaved changes. Switch menu anyway?')
      if (!shouldSwitch) {
        return
      }
    }

    if (menuKey !== DEFAULT_MENU_NAME) {
      setMenuItems([])
      setSavedMenuItems([])
    }

    setSelectedMenuKey(menuKey)
  }

  const handleCustomMenuCreated = (menuKey: string, menuName: string) => {
    setKnownCustomMenuNames((current) => ({ ...current, [menuKey]: menuName }))
    setSelectedMenuKey(menuKey)
    setMenuItems([])
    setSavedMenuItems([])
    setExpandedItems([])
    setIsExpanded(false)
    setRemovedPageIdsPendingConfirmation([])
  }

  const copyMenuLayoutToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(serializeMenuLayoutForClipboard(menuItems))
      toast.success('Menu layout copied to clipboard.')
    } catch (error) {
      console.error('Error copying menu layout to clipboard:', error)
      toast.error('Failed to copy menu layout to clipboard.')
    }
  }

  const pasteMenuLayoutFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText()
      const pastedItems = parseMenuLayoutFromClipboard(clipboardText)

      if (!pastedItems) {
        toast.error('Clipboard does not contain a valid menu layout.')
        return
      }

      setMenuItems(deepCopy(pastedItems))
      setExpandedItems([])
      setIsExpanded(false)
      setRemovedPageIdsPendingConfirmation([])
      toast.success('Menu layout pasted from clipboard. Save to keep your changes.')
    } catch (error) {
      console.error('Error pasting menu layout from clipboard:', error)
      toast.error('Failed to paste menu layout from clipboard.')
    }
  }

  const handleDeleteCustomMenu = async () => {
    if (!applicationId || !isCustomMenu) {
      return false
    }

    const menuKeyToDelete = selectedMenuKey
    const displayNameToDelete = menuOptions.find((o) => o.value === menuKeyToDelete)?.label ?? menuKeyToDelete

    try {
      await deleteCustomMenu({
        path: { applicationId, menuKey: getCustomMenuApiPathParam(menuKeyToDelete) },
      })

      await getUiApiGetCustomMenus.invalidateQueries({ parameters: { path: { applicationId } } })

      if (backendUsesMenuKeys) {
        getUiApiGetCustomMenu.removeQueries({
          parameters: { path: { applicationId, menuKey: getCustomMenuApiPathParam(menuKeyToDelete) } },
        })
      }

      setKnownCustomMenuNames((current) => {
        const next = { ...current }
        delete next[menuKeyToDelete]
        return next
      })
      setSelectedMenuKey(DEFAULT_MENU_NAME)
      setMenuItems(initialDefaultData)
      setSavedMenuItems(initialDefaultData)
      setExpandedItems([])
      setIsExpanded(false)
      setRemovedPageIdsPendingConfirmation([])

      toast.success(`Custom menu "${displayNameToDelete}" deleted.`)
      return true
    } catch (error) {
      console.error('Error deleting custom menu:', error)
      toast.error('Failed to delete custom menu. Please try again.')
      return false
    }
  }

  const persistMenu = async (deletePageIds: string[] = []) => {
    if (!applicationId) return

    setIsSaving(true)

    try {
      if (deletePageIds.length) {
        const currentPages = Array.isArray(existingPages?.items) ? existingPages.items : []
        const updatedPages: MessagesApplicationsApplicationPages = {
          items: currentPages.filter((page) => !deletePageIds.includes(page.id ?? '')),
        }

        await savePages({ query: { applicationId }, body: updatedPages })
        await getAppPagesApiGetApplicationPages.invalidateQueries()
      }

      if (isCustomMenu) {
        const displayName = resolveCustomMenuDisplayName(selectedMenuKey)
        const definition = serializeCustomMenuDefinition(selectedMenuKey, displayName, menuItems)
        const apiPathParam = getCustomMenuApiPathParam(selectedMenuKey, displayName)

        await saveCustomMenu({
          path: { applicationId, menuKey: apiPathParam },
          body: { definition },
        })
        setKnownCustomMenuNames((current) => ({ ...current, [selectedMenuKey]: displayName }))

        if (backendUsesMenuKeys) {
          getUiApiGetCustomMenu.setQueryData(
            { path: { applicationId, menuKey: apiPathParam } },
            (current) => ({
              ...current,
              key: selectedMenuKey,
              name: displayName,
              definition,
            })
          )
        }
      } else {
        await saveMenu({
          path: { applicationId, grouping: 'menu' },
          body: { definition: JSON.stringify({ items: menuItems }) },
        })
      }

      toast.success('Menu saved successfully!')
      setSavedMenuItems(menuItems)
      setRemovedPageIdsPendingConfirmation([])
      await invalidateOnSuccess()
    } catch (error) {
      console.error('Error saving menu:', error)
      toast.error('Failed to save the menu. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = () => {
    const removedPageIds = getRemovedPageIds(savedMenuItems, menuItems)

    if (removedPageIds.length) {
      setRemovedPageIdsPendingConfirmation(removedPageIds)
      return
    }

    void persistMenu()
  }

  const confirmSaveAndDeleteRemovedPages = () => {
    void persistMenu(removedPageIdsPendingConfirmation)
  }

  const saveWithoutDeletingRemovedPages = () => {
    void persistMenu()
  }

  // Toggle expanded state
  const toggleItem = (id: string) => {
    if (expandedItems.includes(id)) {
      setExpandedItems(expandedItems.filter((item) => item !== id))
    } else {
      setExpandedItems([...expandedItems, id])
    }
  }

  const getAllItemIds = (items: MessagesLandingMenuItem[], parentId = ''): string[] => {
    return items.flatMap((item, index) => {
      const id = parentId ? `${parentId}-${index}` : `${index}`
      return [id, ...getAllItemIds(item.items ?? [], id)]
    })
  }

  // Collapse all
  const collapseAll = () => {
    setExpandedItems([])
    setIsExpanded(false)
  }

  // Expand top level
  const expandTopLevel = () => {
    setExpandedItems(getAllItemIds(menuItems))
    setIsExpanded(true)
  }

  // Toggle between expand and collapse
  const toggleExpandCollapse = () => {
    if (isExpanded) {
      collapseAll()
    } else {
      expandTopLevel()
    }
  }

  // Move an item up
  const moveItemUp = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index > 0) {
        // Swap with the previous item
        ;[newData[index], newData[index - 1]] = [newData[index - 1], newData[index]]
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index > 0) {
      // Swap with the previous item
      ;[currentItems[index], currentItems[index - 1]] = [currentItems[index - 1], currentItems[index]]
      setMenuItems(newData)
    }
  }

  // Move an item down
  const moveItemDown = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index < newData.length - 1) {
        // Swap with the next item
        ;[newData[index], newData[index + 1]] = [newData[index + 1], newData[index]]
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index < currentItems.length - 1) {
      // Swap with the next item
      ;[currentItems[index], currentItems[index + 1]] = [currentItems[index + 1], currentItems[index]]
      setMenuItems(newData)
    }
  }

  // Add a child menu item to a parent item
  const addChildItem = (id: string, newItem: MessagesLandingMenuItem) => {
    // Parse the ID to get the path to the parent item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Find the parent item
    let targetItem: MessagesLandingMenuItem | null = null

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        targetItem = newData[index]
      }
    } else {
      // For nested items, navigate to the parent item
      let currentItems = newData

      for (let i = 0; i < path.length; i++) {
        const index = path[i]
        if (currentItems[index]) {
          if (i === path.length - 1) {
            // This is the target item
            targetItem = currentItems[index]
          } else if (currentItems[index].items) {
            // Continue navigating
            currentItems = currentItems[index].items
          } else {
            // If we can't find the item, just return
            return
          }
        } else {
          // Invalid index
          return
        }
      }
    }

    // If we found the target item, add the new child
    if (targetItem) {
      // Initialize items array if it doesn't exist
      if (!targetItem.items) {
        targetItem.items = []
      }

      // Add the new item to the end of the items array
      targetItem.items.push(newItem)

      // Expand the parent to show the new child
      if (!expandedItems.includes(id)) {
        setExpandedItems([...expandedItems, id])
      }

      // Update the data
      setMenuItems(newData)
    }
  }

  const moveItem = (
    sourceId: string,
    target: {
      id: string | null
      position: 'before' | 'after' | 'inside'
    }
  ) => {
    if (target.id && (target.id === sourceId || target.id.startsWith(`${sourceId}-`))) {
      return
    }

    const newData = deepCopy(menuItems)
    const sourcePath = sourceId.split('-').map(Number)
    const removedItem = removeItemAtPath(newData, sourcePath)

    if (!removedItem) {
      return
    }

    if (!target.id) {
      newData.push(removedItem)
      setMenuItems(newData)
      return
    }

    const targetPath = getAdjustedTargetPathAfterRemoval(sourcePath, target.id.split('-').map(Number))

    if (target.position === 'inside') {
      const targetItem = getItemAtPath(newData, targetPath)
      if (!targetItem) {
        return
      }

      targetItem.items = [...(targetItem.items ?? []), removedItem]
      if (!expandedItems.includes(target.id)) {
        setExpandedItems([...expandedItems, target.id])
      }
      setMenuItems(newData)
      return
    }

    insertItemAtSiblingPosition(newData, targetPath, target.position, removedItem)
    setMenuItems(newData)
  }

  // Delete a menu item
  const deleteItem = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        // Remove the item from the top-level array
        newData.splice(index, 1)
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index >= 0 && index < currentItems.length) {
      // Remove the item from the parent array
      currentItems.splice(index, 1)

      // If this item was expanded, remove it from expandedItems
      if (expandedItems.includes(id)) {
        setExpandedItems(expandedItems.filter((itemId) => itemId !== id && !itemId.startsWith(`${id}-`)))
      }

      // Update the data
      setMenuItems(newData)
    }
  }

  // Edit a menu item
  const editItem = (id: string, updatedItem: Partial<MessagesLandingMenuItem>) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Find the target item
    let targetItem: MessagesLandingMenuItem | null = null

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        targetItem = newData[index]
      }
    } else {
      // For nested items, navigate to find the item
      let currentItems = newData

      for (let i = 0; i < path.length; i++) {
        const index = path[i]
        if (i === path.length - 1) {
          // This is the target item
          if (index >= 0 && index < currentItems.length) {
            targetItem = currentItems[index]
          }
        } else if (currentItems[index] && currentItems[index].items) {
          // Continue navigating
          currentItems = currentItems[index].items
        } else {
          // If we can't find the item, just return
          return
        }
      }
    }

    // If we found the target item, update its properties
    if (targetItem) {
      // Update each property that was provided
      Object.keys(updatedItem).forEach((key) => {
        const typedKey = key as keyof MessagesLandingMenuItem
        if (updatedItem[typedKey] !== undefined) {
          // Preserve the items array to maintain the hierarchy
          if (typedKey !== 'items') {
            // Use type assertion to avoid 'any' type
            ;(targetItem as Record<keyof MessagesLandingMenuItem, unknown>)[typedKey] = updatedItem[typedKey]
          }
        }
      })

      // Update the data
      setMenuItems(newData)
    }
  }

  // Add a new root level menu item
  const addRootItem = (newItem: MessagesLandingMenuItem) => {
    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Add the new item to the end of the root level array
    newData.push(newItem)

    // Update the data
    setMenuItems(newData)
  }

  return {
    menuItems,
    setMenuItems,
    expandedItems,
    isExpanded,
    hasChanged,
    initialData: savedMenuItems,
    removedPageIdsPendingConfirmation,
    isSaving,
    isLoadingCustomMenu,
    isDeletingCustomMenu,
    isCustomMenu,
    selectedMenuName: selectedMenuKey,
    menuOptions,
    customMenuNames: customMenuKeys,
    handleMenuSelectionChange,
    handleCustomMenuCreated,
    getCustomMenuApiPathParam,
    copyMenuLayoutToClipboard,
    pasteMenuLayoutFromClipboard,
    handleDeleteCustomMenu,
    toggleItem,
    collapseAll,
    expandTopLevel,
    toggleExpandCollapse,
    moveItemUp,
    moveItemDown,
    addChildItem,
    deleteItem,
    editItem,
    addRootItem,
    moveItem,
    handleSave,
    confirmSaveAndDeleteRemovedPages,
    saveWithoutDeletingRemovedPages,
    setRemovedPageIdsPendingConfirmation,
  }
}

function getRemovedPageIds(
  originalItems: MessagesLandingMenuItem[],
  nextItems: MessagesLandingMenuItem[]
) {
  const originalPageIds = getPageIds(originalItems)
  const nextPageIds = getPageIds(nextItems)

  return originalPageIds.filter((pageId) => !nextPageIds.includes(pageId))
}

function getPageIds(items: MessagesLandingMenuItem[]): string[] {
  const pageIds = new Set<string>()

  const collectPageIds = (menuItems: MessagesLandingMenuItem[]) => {
    menuItems.forEach((item) => {
      const pageId = item.page_id?.trim()

      if (pageId) {
        pageIds.add(pageId)
      }

      collectPageIds(item.items ?? [])
    })
  }

  collectPageIds(items)

  return Array.from(pageIds)
}

function getItemAtPath(items: MessagesLandingMenuItem[], path: number[]) {
  let currentItems = items

  for (let index = 0; index < path.length; index++) {
    const pathPart = path[index]
    const item = currentItems[pathPart]

    if (!item) {
      return undefined
    }

    if (index === path.length - 1) {
      return item
    }

    currentItems = item.items ?? []
  }

  return undefined
}

function removeItemAtPath(items: MessagesLandingMenuItem[], path: number[]) {
  if (path.length === 1) {
    const [removedItem] = items.splice(path[0], 1)
    return removedItem
  }

  const parent = getItemAtPath(items, path.slice(0, -1))
  if (!parent?.items) {
    return undefined
  }

  const [removedItem] = parent.items.splice(path[path.length - 1], 1)
  return removedItem
}

function insertItemAtSiblingPosition(
  items: MessagesLandingMenuItem[],
  targetPath: number[],
  position: 'before' | 'after',
  item: MessagesLandingMenuItem
) {
  const targetIndex = targetPath[targetPath.length - 1]
  const insertIndex = position === 'before' ? targetIndex : targetIndex + 1

  if (targetPath.length === 1) {
    items.splice(insertIndex, 0, item)
    return
  }

  const parent = getItemAtPath(items, targetPath.slice(0, -1))
  if (!parent) {
    return
  }

  parent.items = parent.items ?? []
  parent.items.splice(insertIndex, 0, item)
}

function getAdjustedTargetPathAfterRemoval(sourcePath: number[], targetPath: number[]) {
  const adjustedTargetPath = [...targetPath]
  const sameParent =
    sourcePath.length === targetPath.length &&
    sourcePath.slice(0, -1).every((pathPart, index) => pathPart === targetPath[index])

  if (sameParent && sourcePath[sourcePath.length - 1] < targetPath[targetPath.length - 1]) {
    adjustedTargetPath[adjustedTargetPath.length - 1] -= 1
  }

  return adjustedTargetPath
}
